import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errDetail } from "../lib/api";
import { Lock, UserPlus, RefreshCw } from "lucide-react";
import { Clock } from "../components/Clock";
import { useLang } from "../context/LanguageContext";
import { LangToggle } from "../components/LangToggle";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const SLIDE_IMAGES = [
  "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?auto=format&fit=crop&w=1600&q=80",
];

function Carousel({ platformName, logoUrl }) {
  const { t } = useLang();
  const captions = [t("login.slide1"), t("login.slide2"), t("login.slide3"), t("login.slide4")];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const tm = setInterval(() => setIdx((i) => (i + 1) % SLIDE_IMAGES.length), 5000);
    return () => clearInterval(tm);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="login-carousel">
      {SLIDE_IMAGES.map((url, i) => (
        <img key={url} src={url} alt=""
          onError={(e) => { e.target.style.display = "none"; }}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ opacity: i === idx ? 1 : 0 }} />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-slate-950/30" />
      <div className="relative z-10 h-full flex flex-col justify-between p-8 lg:p-12">
        <div className="bg-white rounded-2xl shadow-xl p-4 w-fit" data-testid="login-logo-card">
          {logoUrl && <img src={logoUrl} alt="logo" className="h-16 lg:h-24 object-contain" />}
        </div>
        <div className="space-y-4 max-w-md">
          <h1 className="text-3xl lg:text-5xl font-bold font-heading leading-tight text-white drop-shadow-lg">{platformName}</h1>
          <p key={idx} className="text-slate-100 text-base lg:text-lg fade-up drop-shadow" data-testid="login-slide-caption">{captions[idx]}</p>
          <div className="flex gap-2 pt-1" data-testid="login-carousel-dots">
            {captions.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} aria-label={`slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-amber-500" : "w-3 bg-white/50 hover:bg-white/80"}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { login, settings } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const [showReg, setShowReg] = useState(false);
  const [showRenew, setShowRenew] = useState(false);
  const [regBusy, setRegBusy] = useState(false);
  const [cats, setCats] = useState([]);
  const [reg, setReg] = useState({ name: "", country: "", email: "", phone: "", allowed_categories: [] });
  const [regCountry, setRegCountry] = useState("");
  const [renewEmail, setRenewEmail] = useState("");
  const COUNTRIES = ["BELICE", "COLOMBIA", "COSTA RICA", "CURAZAO", "ECUADOR", "EL SALVADOR", "GUATEMALA", "GUYANA", "HAITI", "HONDURAS", "ISLAS VIRGENES", "JAMAICA", "MEXICO", "NICARAGUA", "PANAMA / ZONA LIBRE", "REP. DOMINICANA", "ST. LUCIA", "ST. VINCENT", "SURINAME", "TRINIDAD", "VENEZUELA"];
  const DIAL_CODES = { "BELICE": "+501", "COLOMBIA": "+57", "COSTA RICA": "+506", "CURAZAO": "+599", "ECUADOR": "+593", "EL SALVADOR": "+503", "GUATEMALA": "+502", "GUYANA": "+592", "HAITI": "+509", "HONDURAS": "+504", "ISLAS VIRGENES": "+1340", "JAMAICA": "+1876", "MEXICO": "+52", "NICARAGUA": "+505", "PANAMA / ZONA LIBRE": "+507", "REP. DOMINICANA": "+1809", "ST. LUCIA": "+1758", "ST. VINCENT": "+1784", "SURINAME": "+597", "TRINIDAD": "+1868", "VENEZUELA": "+58" };

  const openReg = () => { setReg({ name: "", country: "", email: "", phone: "", allowed_categories: [] }); setRegCountry(""); setShowReg(true); api.get("/public/categories").then(({ data }) => setCats(data)).catch(() => {}); };
  const toggleCat = (c) => setReg((r) => ({ ...r, allowed_categories: r.allowed_categories.includes(c) ? r.allowed_categories.filter((x) => x !== c) : [...r.allowed_categories, c] }));
  const submitReg = async (e) => { e.preventDefault(); setRegBusy(true); try { await api.post("/register", reg); toast.success("Solicitud enviada. Revise su correo; pronto recibirá su usuario y contraseña."); setShowReg(false); } catch (err) { toast.error(errDetail(err)); } finally { setRegBusy(false); } };
  const submitRenew = async (e) => { e.preventDefault(); setRegBusy(true); try { await api.post("/account/renew", { email: renewEmail }); toast.success("Solicitud de renovación enviada. Recibirá un correo cuando su cuenta esté activada."); setShowRenew(false); setRenewEmail(""); } catch (err) { toast.error(errDetail(err)); } finally { setRegBusy(false); } };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      navigate("/catalogo");
    } catch (err) {
      toast.error(errDetail(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background">
      <div className="relative hidden lg:block lg:w-3/5">
        <Carousel platformName={settings.platform_name} logoUrl={settings.logo_url} />
      </div>

      {/* Mobile hero with carousel */}
      <div className="relative lg:hidden h-64">
        <Carousel platformName={settings.platform_name} logoUrl={settings.logo_url} />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-10 lg:py-12">
        <div className="w-full max-w-sm flex justify-end mb-2"><LangToggle className="border border-border" /></div>
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <div className="space-y-1">
            <h2 className="text-2xl font-bold font-heading flex items-center gap-2"><Lock size={20} className="text-amber-600" /> {t("login.title")}</h2>
            <p className="text-sm text-muted-foreground">{t("login.subtitle")}</p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">{t("login.email")}</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input" placeholder={t("login.emailPlaceholder")}
                className="w-full px-4 py-3 rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-amber-600" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">{t("login.password")}</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input" placeholder="••••••••"
                className="w-full px-4 py-3 rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-amber-600" />
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="login-submit-button"
            className="w-full py-3 rounded-xl bg-slate-900 dark:bg-sky-500 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
            {busy ? t("login.submitting") : t("login.submit")}
          </button>
          <div className="text-center space-y-3">
            <p className="text-sm sm:text-base text-foreground/80" data-testid="login-no-account">
              ¿No tiene cuenta? El registro lo realiza el administrador. Deje sus datos aquí.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button type="button" onClick={openReg} data-testid="open-register-button"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-amber-600 text-white text-sm font-bold hover:bg-amber-700 transition-colors"><UserPlus size={16} /> REGISTRARSE</button>
              <button type="button" onClick={() => setShowRenew(true)} data-testid="open-renew-button"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-border text-sm font-semibold hover:bg-muted transition-colors"><RefreshCw size={15} /> Renovar acceso</button>
            </div>
          </div>
          <Clock className="justify-center text-muted-foreground pt-2 border-t border-border" />
        </form>

        <Dialog open={showReg} onOpenChange={setShowReg}>
          <DialogContent data-testid="register-dialog" className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Registrarse</DialogTitle></DialogHeader>
            <form onSubmit={submitReg} className="space-y-3">
              <input required value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} placeholder="Nombre comercial" data-testid="reg-name-input" className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
              <select value={regCountry} onChange={(e) => { const v = e.target.value; setRegCountry(v); const dc = DIAL_CODES[v] || ""; setReg({ ...reg, country: v === "OTRO" ? "" : v, phone: (v && v !== "OTRO" && dc) ? dc + " " : reg.phone }); }} data-testid="reg-country-select" className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600">
                <option value="">País</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="OTRO">OTRO (ESCRIBIR MANUALMENTE)</option>
              </select>
              {regCountry === "OTRO" && <input value={reg.country} onChange={(e) => setReg({ ...reg, country: e.target.value })} placeholder="Escriba el país" data-testid="reg-country-other" className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />}
              <input required type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} placeholder="Email" data-testid="reg-email-input" className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
              <input value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} placeholder="Teléfono" data-testid="reg-phone-input" className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
              <div>
                <p className="text-sm font-medium mb-1">Categorías de interés</p>
                <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto" data-testid="reg-categories">
                  <button type="button" onClick={() => setReg((r) => ({ ...r, allowed_categories: [] }))} data-testid="reg-cat-todas"
                    className={`px-3.5 py-1.5 rounded-full text-sm font-bold border transition-colors ${reg.allowed_categories.length === 0 ? "bg-amber-600 text-white border-transparent" : "border-amber-600 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"}`}>TODAS</button>
                  {cats.map((c) => (
                    <button type="button" key={c} onClick={() => toggleCat(c)}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${reg.allowed_categories.includes(c) ? "bg-amber-600 text-white border-transparent" : "border-border hover:bg-muted"}`}>{c}</button>
                  ))}
                </div>
              </div>
              <button type="submit" disabled={regBusy} data-testid="reg-submit-button" className="w-full py-2.5 rounded-full bg-amber-600 text-white text-sm font-bold disabled:opacity-50">{regBusy ? "Enviando..." : "Enviar solicitud"}</button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showRenew} onOpenChange={setShowRenew}>
          <DialogContent data-testid="renew-dialog">
            <DialogHeader><DialogTitle>Renovar acceso</DialogTitle></DialogHeader>
            <form onSubmit={submitRenew} className="space-y-3">
              <p className="text-sm text-muted-foreground">Ingrese su email. Enviaremos su solicitud al administrador y recibirá un correo cuando su cuenta esté activada.</p>
              <input required type="email" value={renewEmail} onChange={(e) => setRenewEmail(e.target.value)} placeholder="Su email" data-testid="renew-email-input" className="w-full px-3 py-2 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
              <button type="submit" disabled={regBusy} data-testid="renew-submit-button" className="w-full py-2.5 rounded-full bg-slate-900 dark:bg-sky-500 text-white text-sm font-semibold disabled:opacity-50">{regBusy ? "Enviando..." : "Enviar solicitud"}</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
